import React, { useState, useEffect, useCallback } from 'react';
import AnswerFields from '../../components/Admin/AnswerFields';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import { getVariant, updateVariant, createHomework, updateHomework, deleteHomework } from '../../services/adminService';
import { confirmDialog } from '../../components/Common/confirm';
import toast from 'react-hot-toast';

// Баллы по умолчанию: ЕГЭ №1–25 = 1, №26–27 = 2. ОГЭ — вручную (1).
const defaultMax = (examType, idx0) => (examType === 'ЕГЭ' && idx0 >= 25 ? 2 : 1);

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

const inputStyle = { width: '100%', padding: '11px 13px', borderRadius: 11, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-1)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };

const AdminVariantEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [nomera, setNomera] = useState([]);   // список номеров (Homework)
  const [active, setActive] = useState(0);     // индекс выбранного номера
  const [form, setForm] = useState({ description: '', correctAnswer: '', maxScore: 1, manualReview: false, anyOfAnswers: false });
  const [loading, setLoading] = useState(true);
  const [savingTitle, setSavingTitle] = useState(false);
  const [title, setTitle] = useState('');

  const load = useCallback(async (selectIdx) => {
    const res = await getVariant(id);
    const v = res.data;
    setVariant(v);
    setTitle(v.title || '');
    const list = v.Homeworks || v.Homework || [];
    setNomera(list);
    setActive(prev => {
      const idx = selectIdx != null ? selectIdx : prev;
      return Math.max(0, Math.min(idx, list.length - 1));
    });
    return list;
  }, [id]);

  useEffect(() => { load().catch(() => {}).finally(() => setLoading(false)); }, [load]);

  // При смене активного номера — подставляем его поля в форму
  useEffect(() => {
    const n = nomera[active];
    if (n) setForm({ description: n.description || '', correctAnswer: n.correctAnswer || '', maxScore: n.maxScore ?? 1, manualReview: !!n.manualReview, anyOfAnswers: !!n.anyOfAnswers });
  }, [active, nomera]);

  const saveCurrent = async () => {
    const n = nomera[active];
    if (!n) return;
    await updateHomework(n.id, {
      description: form.description,
      correctAnswer: form.manualReview ? '' : form.correctAnswer,
      anyOfAnswers: !form.manualReview && !!form.anyOfAnswers,
      manualReview: !!form.manualReview,
      maxScore: Number(form.maxScore) || 1,
    });
  };

  const selectNomer = async (idx) => {
    if (idx === active) return;
    try { await saveCurrent(); } catch (e) { /* не блокируем переключение */ }
    setActive(idx);
    await load(idx);
  };

  const addNomer = async () => {
    try { await saveCurrent(); } catch (e) {}
    const idx0 = nomera.length;
    await createHomework({
      lessonId: Number(id),
      title: String(idx0 + 1),
      maxScore: defaultMax(variant?.examType, idx0),
      answerType: 'single',
      sortOrder: idx0,
    });
    await load(idx0);
    toast.success('Номер добавлен');
  };

  const removeNomer = async () => {
    const n = nomera[active];
    if (!n) return;
    if (!(await confirmDialog({ title: 'Удалить номер?', message: `№${active + 1} будет удалён.`, confirmText: 'Удалить' }))) return;
    await deleteHomework(n.id);
    await load(Math.max(0, active - 1));
    toast.success('Номер удалён');
  };

  const saveNomer = async () => {
    try { await saveCurrent(); await load(active); toast.success('Номер сохранён'); }
    catch { toast.error('Ошибка сохранения'); }
  };

  const saveTitle = async () => {
    if (!title.trim() || title === variant?.title) return;
    setSavingTitle(true);
    try { await updateVariant(id, { title: title.trim() }); toast.success('Название сохранено'); }
    catch { toast.error('Ошибка'); }
    finally { setSavingTitle(false); }
  };

  if (loading) return <Loader />;
  if (!variant) return <div className="pg-empty">Вариант не найден</div>;

  const maxPrimary = nomera.reduce((s, n) => s + (n.maxScore ?? 1), 0);
  const n = nomera[active];

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Header */}
      <button onClick={async () => { try { await saveCurrent(); } catch (e) {} navigate('/admin/variants'); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 14, padding: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        К списку вариантов
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <input value={title} onChange={e => setTitle(e.target.value)} onBlur={saveTitle}
          style={{ ...inputStyle, width: 'auto', flex: 1, minWidth: 220, fontSize: 22, fontWeight: 800, border: '1px solid transparent', background: 'transparent', padding: '4px 6px' }} />
        {savingTitle && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>сохранение…</span>}
      </div>
      <p className="s-page-sub" style={{ marginBottom: 18 }}>
        {variant.examType ? `${variant.examType} · ` : ''}{nomera.length} {plural(nomera.length, ['номер', 'номера', 'номеров'])} · макс. {maxPrimary} {plural(maxPrimary, ['первичный', 'первичных', 'первичных'])}
      </p>

      {/* Окошки номеров */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
        {nomera.map((nm, i) => {
          const on = i === active;
          // точка в углу — номер с ручной проверкой, чтобы их было видно списком
          const man = !!nm?.manualReview;
          return (
            <button key={i} onClick={() => selectNomer(i)} title={man ? 'Ручная проверка' : undefined}
              style={{
                position: 'relative',
                width: 42, height: 42, borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                background: on ? 'var(--primary)' : 'var(--card)', color: on ? '#fff' : 'var(--text-2)',
                boxShadow: on ? '0 6px 16px -6px rgba(124,77,255,0.6)' : 'none', transition: 'all .15s',
              }}>
              {i + 1}
              {man && <span style={{ position: 'absolute', top: 5, right: 5, width: 6, height: 6, borderRadius: '50%', background: on ? '#fff' : 'var(--primary)' }} />}
            </button>
          );
        })}
        <button onClick={addNomer} title="Добавить номер"
          style={{ width: 42, height: 42, borderRadius: 12, cursor: 'pointer', border: '1px dashed var(--primary)', background: 'transparent', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      </div>

      {/* Редактирование номера */}
      {!n ? (
        <div className="pg-empty">В варианте пока нет номеров. Нажмите «+», чтобы добавить.</div>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)' }}>Номер {active + 1}</div>
            <button onClick={removeNomer} style={{ background: 'rgba(229,72,77,0.10)', color: '#e5484d', border: 'none', borderRadius: 9, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Удалить номер</button>
          </div>

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Условие задания</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={4} placeholder="Текст задания (можно вставить условие)…" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: '1 1 260px' }}>
              <label className="form-label">Правильный ответ</label>
              {form.manualReview ? (
                <div className="hn-manual-note">
                  Эталонного ответа нет: ученик присылает решение целиком — текст, файлы и комментарий.
                  Баллы и замечание ставит куратор в разделе «Проверка ДЗ».
                </div>
              ) : (
                <>
                  <AnswerFields
                    value={form.correctAnswer}
                    onChange={(v) => setForm(f => ({ ...f, correctAnswer: v }))}
                    anyOf={form.anyOfAnswers}
                    onAnyOfChange={(v) => setForm(f => ({ ...f, anyOfAnswers: v }))}
                    inputStyle={inputStyle} />
                  {!form.anyOfAnswers && (
                    <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                      Для №26–27 (два ответа) — с новой строки или через «;».
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="form-group" style={{ width: 120 }}>
              <label className="form-label">Баллы</label>
              <input type="number" min="1" value={form.maxScore} onChange={e => setForm(f => ({ ...f, maxScore: e.target.value }))} style={inputStyle} />
            </div>
          </div>

          {/* Ручная проверка: у номера нет эталона, работу смотрит человек */}
          <button type="button" className={`hn-manual${form.manualReview ? ' on' : ''}`}
            onClick={() => setForm(f => ({ ...f, manualReview: !f.manualReview, correctAnswer: f.manualReview ? f.correctAnswer : '' }))}>
            <span className="hn-manual-dot" />
            Ручная проверка
            <span className="hn-manual-sub">{form.manualReview ? 'баллы ставит куратор' : 'ответ сверяется автоматически'}</span>
          </button>

          <div style={{ marginTop: 8 }}>
            <button className="action-btn purple" onClick={saveNomer}>Сохранить номер</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVariantEditPage;
